/**
 * Job priority + time-to-claim model for the tester queue.
 *
 * Priority is driven primarily by the creating organization's **plan tier**.
 * How the job was created (its {@link JobSource}) is only a minor modifier, and
 * first-party (internal Runhuman) jobs are deprioritized so paying customers
 * come first. Two outputs are derived from the same inputs:
 *
 *  - {@link computePriorityScore} — a numeric score that orders the queue
 *    (higher = claimed first). Stored on the job; projected to a coarse
 *    {@link PriorityBand} for notification routing and badges.
 *  - {@link computeClaimWindowMinutes} — how long an unclaimed job waits before
 *    expiring, bounded to [30 min, 6 h]. Higher-priority jobs get shorter,
 *    more urgent windows; deprioritized jobs get longer, more patient ones.
 */
// ────────────────────────────── Priority score ──────────────────────────────
/**
 * Tier is the spine of the score. Even 20-point steps keep tier dominant: a
 * relax modifier never flips two tiers, but the internal penalty intentionally
 * sinks a first-party job well below an equivalent customer's.
 */
export const TIER_PRIORITY = {
    free: 0,
    starter: 20,
    pro: 40,
    max: 60,
    enterprise: 80,
    enterprise_pro: 100,
};
/**
 * Sources with no per-job urgency: planned (scheduler) or batch
 * (bulk issue testing) work where nobody is waiting on a single result.
 */
export const RELAXED_SOURCES = new Set([
    'scheduler',
    'bulk-test-queue',
]);
/** Score penalty applied to relaxed sources (less than one tier step). */
export const RELAX_SCORE_PENALTY = -10;
/** Score penalty applied to internal jobs (~2.5 tiers — customers come first). */
export const INTERNAL_SCORE_PENALTY = -50;
function isRelaxedSource(source) {
    return source !== undefined && RELAXED_SOURCES.has(source);
}
/**
 * Compute a job's numeric priority score. Higher = claimed first. The score may
 * go negative (a free + relaxed + internal job lands at -60), which still orders
 * correctly below a plain free job at 0.
 */
export function computePriorityScore(inputs) {
    return (TIER_PRIORITY[inputs.tier] +
        (isRelaxedSource(inputs.source) ? RELAX_SCORE_PENALTY : 0) +
        (inputs.isInternal ? INTERNAL_SCORE_PENALTY : 0));
}
// ─────────────────────────────── Claim window ───────────────────────────────
/**
 * Per-tier base claim window, shortest → longest. The curve is deliberately
 * non-linear: a gradual 30-min climb across the premium tiers (30 min → 2 h),
 * then a rapid climb to the 6 h cap for the lower tiers.
 */
export const CLAIM_WINDOW_MINUTES_BY_TIER = {
    enterprise_pro: 30,
    enterprise: 60,
    max: 90,
    pro: 120,
    starter: 240,
    free: 360,
};
/** Tiers ordered by ascending claim window (index 0 = shortest = highest priority). */
export const TIER_WINDOW_ORDER = [
    'enterprise_pro',
    'enterprise',
    'max',
    'pro',
    'starter',
    'free',
];
/**
 * CI-driven sources whose claim window is fixed rather than tier-derived. A PR
 * check runs in a narrow automated window — once the run has moved on, an
 * unclaimed job lingering for hours is useless — so these always expire at
 * {@link CI_CLAIM_WINDOW_MINUTES}, ignoring tier and the relax/internal
 * modifiers entirely.
 */
export const CI_SOURCES = new Set([
    'qa-test-action',
    'issue-tester-action',
]);
/**
 * Fixed claim window (minutes) for {@link CI_SOURCES}. A flat constant on
 * purpose — it is not derived from any tier's window, so retuning a tier's
 * timing can never silently move the CI window.
 */
export const CI_CLAIM_WINDOW_MINUTES = 30;
function isCiSource(source) {
    return source !== undefined && CI_SOURCES.has(source);
}
/**
 * Compute the claim window in minutes. CI sources are pinned to a fixed window
 * regardless of tier or modifiers. Otherwise relaxed sources and internal orgs
 * are "more patient": each one steps the job one tier slower (a longer window),
 * clamped at the free-tier 6 h ceiling.
 */
export function computeClaimWindowMinutes(inputs) {
    if (isCiSource(inputs.source))
        return CI_CLAIM_WINDOW_MINUTES;
    const lengthenSteps = (isRelaxedSource(inputs.source) ? 1 : 0) + (inputs.isInternal ? 1 : 0);
    const baseIndex = TIER_WINDOW_ORDER.indexOf(inputs.tier);
    const effectiveIndex = Math.min(baseIndex + lengthenSteps, TIER_WINDOW_ORDER.length - 1);
    return CLAIM_WINDOW_MINUTES_BY_TIER[TIER_WINDOW_ORDER[effectiveIndex]];
}
/** Compute the absolute expiry timestamp from creation time and a claim window. */
export function computeExpiresAt(createdAt, claimWindowMinutes) {
    return new Date(new Date(createdAt).getTime() + claimWindowMinutes * 60 * 1000).toISOString();
}
/**
 * Weight of the aging term in {@link computeQueueScore}. Deliberately smaller
 * than the 20-point gap between adjacent tiers: aging only ever reorders
 * *within* a priority tier and applies gentle cross-tier pressure — it can
 * never lift a fully-aged lower-tier job above a fresh higher-tier one. That
 * keeps the queue **priority-dominant**: lower-tier jobs are allowed to expire
 * unclaimed when testers are saturated.
 */
export const QUEUE_SCORE_AGING_WEIGHT = 15;
/**
 * How far a job is through its own claim window, in `[0, 1]`. Normalizing to
 * each job's own window (not a global clock) lets a 30-min enterprise job and
 * a 6-h free job age on the same scale — the short window simply fills faster.
 * Clamped at both ends: clock skew before creation reads 0, past expiry reads 1.
 */
export function computeElapsedFraction(createdAt, expiresAt, nowMs) {
    const startMs = new Date(createdAt).getTime();
    const endMs = new Date(expiresAt).getTime();
    const fraction = (nowMs - startMs) / (endMs - startMs);
    if (fraction < 0)
        return 0;
    if (fraction > 1)
        return 1;
    return fraction;
}
/**
 * Dynamic, time-aware queue score (#5115). Priority is the spine; the job's
 * elapsed fraction of its own window is a bounded additive nudge. Higher =
 * surfaced first. Recompute at query/render time — it is never stored.
 */
export function computeQueueScore(inputs, nowMs) {
    return (inputs.priorityScore +
        QUEUE_SCORE_AGING_WEIGHT * computeElapsedFraction(inputs.createdAt, inputs.expiresAt, nowMs));
}
/**
 * Total order for the tester queue, shared by the server snapshot and the
 * client per-tester sort so the two can never diverge. Higher
 * {@link computeQueueScore} first; ties broken by the soonest absolute expiry.
 */
export function compareQueueByScore(a, b, nowMs) {
    const scoreDiff = computeQueueScore(b, nowMs) - computeQueueScore(a, nowMs);
    if (scoreDiff !== 0)
        return scoreDiff;
    return new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime();
}
// ─────────────────────────────── Priority band ──────────────────────────────
/**
 * Coarse band projected from a {@link computePriorityScore}. Used for tester
 * notification preferences ("notify me for High/Medium/Low jobs") and badges —
 * the discrete, human-facing face of the continuous score.
 */
export const PRIORITY_BANDS = ['high', 'medium', 'low'];
/** Score at/above which a job is "high" band. */
export const HIGH_BAND_MIN_SCORE = 70;
/** Score at/above which a job is "medium" band (below ⇒ "low"). */
export const MEDIUM_BAND_MIN_SCORE = 30;
export function priorityScoreToBand(priorityScore) {
    if (priorityScore >= HIGH_BAND_MIN_SCORE)
        return 'high';
    if (priorityScore >= MEDIUM_BAND_MIN_SCORE)
        return 'medium';
    return 'low';
}
export function isPriorityBand(value) {
    return typeof value === 'string' && PRIORITY_BANDS.includes(value);
}
//# sourceMappingURL=job-priority.types.js.map